import type { Fetcher, Middleware } from 'openapi-typescript-fetch';
import { createContext, type ReactNode, useContext } from 'react';
import type { Environment } from '../..';
import type { paths as bilholdPaths } from '../schema/bilhold-api';
import type { paths as damageAppraisalPaths } from '../schema/damage-appraisal';
import type { paths as portPaths } from '../schema/port';
import { configureBilholdFetcher } from './bilhold';
import { configureDamageAppraisalFetcher } from './damageappraisal';
import { configurePortFetcher } from './port';

const FetchersContext = createContext<Fetchers | null>(null);

export type FetcherConfiguration = {
    environment: Environment;
    middlewares?: Middleware[];
    localEnvironmentOverride?: string;
};

type Fetchers = {
    port: {
        baseUrl: string;
        fetcher: ReturnType<typeof Fetcher.for<portPaths>>;
        environment: Environment;
    };
    bilhold: {
        baseUrl: string;
        fetcher: ReturnType<typeof Fetcher.for<bilholdPaths>>;
        environment: Environment;
    };
    damageappraisal: {
        baseUrl: string;
        fetcher: ReturnType<typeof Fetcher.for<damageAppraisalPaths>>;
        environment: Environment;
    };
};

type FetcherConfigs = {
    [key in keyof Fetchers]: {
        localEnvironmentOverride: string | undefined;
        middlewares: Middleware[];
    };
};

type FetcherProviderProps = {
    children: ReactNode;
    environment: Environment;
    configs: FetcherConfigs;
};

export function FetchersProvider({
    children,
    environment,
    configs,
}: FetcherProviderProps) {
    const fetchers: Fetchers = {
        port: configurePortFetcher({ ...configs.port, environment }),
        bilhold: configureBilholdFetcher({ ...configs.bilhold, environment }),
        damageappraisal: configureDamageAppraisalFetcher({
            ...configs.damageappraisal,
            environment,
        }),
    };
    return (
        <FetchersContext.Provider value={fetchers}>
            {children}
        </FetchersContext.Provider>
    );
}

export function useFetchersContext() {
    const fetchers = useContext(FetchersContext);

    if (!fetchers) {
        throw new Error(
            'Fetchers context is undefined. UseFetchersContext must be used within a FetchersProvider',
        );
    }

    return [fetchers] as const;
}
